package com.example.mystybag.common.ui.views

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentWidth
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.common.logic.DialogNavigatorStandard
import com.example.mystybag.common.expected.setWebDialogOpen
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.core.extensions.capitalizeFirst
import com.example.mystybag.core.logic.helpers.DialogResultType
import com.example.mystybag.core.logic.helpers.UIDialogInterface
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme

// Figma Frame 26080626: 348×264, cornerRadius 40, padding 32/28, itemSpacing 24 (text block 12, buttons 8)
private val LogoutDialogWidthDp = 348.dp
private val LogoutDialogCornerRadiusDp = 40.dp
private val LogoutDialogPaddingHorizontalDp = 32.dp
private val LogoutDialogPaddingVerticalDp = 28.dp
private val LogoutDialogContentSpacingDp = 24.dp
private val LogoutDialogTextBlockSpacingDp = 12.dp
private val LogoutDialogButtonsSpacingDp = 8.dp
private val LogoutDialogButtonHeightDp = 56.dp

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AlertDialogView(viewModel: UIDialogInterface) {

    val dialogState = viewModel.dialogState.subscribeAsState()

    dialogState.value.child?.instance?.also { dialog ->
        DisposableEffect(Unit) {
            setWebDialogOpen(true)
            onDispose { setWebDialogOpen(false) }
        }

        val useStackedLayout = (dialog as? DialogNavigatorStandard)?.stackedButtons == true

        BasicAlertDialog(
            onDismissRequest    = {
                viewModel.dismissDialog(DialogResultType.CANCELED)
            },
            modifier            = Modifier.padding(p4),
            properties          = DialogProperties(
                dismissOnBackPress    = dialog.closable,
                dismissOnClickOutside = dialog.closable
            ),
            content             = {
                if (useStackedLayout) {
                    Column(
                        modifier = Modifier
                            .widthIn(max = LogoutDialogWidthDp)
                            .clip(RoundedCornerShape(LogoutDialogCornerRadiusDp))
                            .background(AppTheme.current.colors.surface)
                            .padding(
                                start = LogoutDialogPaddingHorizontalDp,
                                top = LogoutDialogPaddingVerticalDp,
                                end = LogoutDialogPaddingHorizontalDp,
                                bottom = LogoutDialogPaddingVerticalDp
                            )
                    ) {
                        // Frame 26080632: title + message, itemSpacing 12
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(LogoutDialogTextBlockSpacingDp)
                        ) {
                            dialog.title?.let { Texts.HeadlineMedium(text = it) }
                            dialog.clickableText?.let { texts ->
                                Texts.ClickableTextView(
                                    texts       = texts,
                                    textStyle   = AppTheme.current.typography.labelLarge,
                                    onItemClick = { index -> viewModel.dismissDialog(DialogResultType.LINK_TEXT, texts[index]) }
                                )
                            } ?: dialog.coloredMessage?.let { texts ->
                                Texts.ColoredTextView(
                                    texts       = texts,
                                    textStyle   = AppTheme.current.typography.labelLarge
                                )
                            } ?: Texts.LabelLarge(
                                text  = dialog.message,
                                color = AppTheme.current.colors.onSurfaceVariant
                            )
                        }

                        Spacer(modifier = Modifier.height(LogoutDialogContentSpacingDp))

                        // Frame 26080633: buttons, itemSpacing 8, each 56dp height, cornerRadius 100
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(LogoutDialogButtonsSpacingDp)
                        ) {
                            DefaultButton(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .height(LogoutDialogButtonHeightDp),
                                caption = if (dialog.autoBtnCapital.positiveButton)
                                    dialog.positiveTitle.lowercase().capitalizeFirst()
                                else
                                    dialog.positiveTitle,
                                type = DefaultButtonType.PRIMARY,
                                callback = { viewModel.dismissDialog(DialogResultType.POSITIVE) }
                            )
                            dialog.negativeTitle?.let { title ->
                                DefaultButton(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(LogoutDialogButtonHeightDp),
                                    caption = if (dialog.autoBtnCapital.negativeButton)
                                        title.lowercase().capitalizeFirst()
                                    else
                                        title,
                                    type = DefaultButtonType.SECONDARY_NEUTRAL,
                                    callback = { viewModel.dismissDialog(DialogResultType.NEGATIVE) }
                                )
                            }
                        }
                    }
                } else {
                    Column(
                        modifier            = Modifier
                            .clip(RoundedCornerShape(p16))
                            .background(AppTheme.current.colors.surface)
                            .padding(start = p20, top = p20, end = p20, bottom = p8)
                            .verticalScroll(rememberScrollState())
                    ) {
                        dialog.title?.let { Texts.HeadlineMedium(modifier = Modifier.padding(bottom = p16), text = it) }

                        dialog.clickableText?.let { texts ->
                            Texts.ClickableTextView(
                                texts       = texts,
                                textStyle   = AppTheme.current.typography.labelLarge,
                                onItemClick = { index -> viewModel.dismissDialog(DialogResultType.LINK_TEXT, texts[index]) }
                            )
                        } ?: dialog.coloredMessage?.let { texts ->
                            Texts.ColoredTextView(
                                texts       = texts,
                                textStyle   = AppTheme.current.typography.labelLarge
                            )
                        } ?: Texts.LabelLarge(
                            text    = dialog.message,
                            color   = AppTheme.current.colors.onSurfaceVariant
                        )

                        Row(
                            modifier                = Modifier
                                .fillMaxWidth()
                                .padding(top = p8),
                            horizontalArrangement   = Arrangement.spacedBy(space = p16, alignment = Alignment.End)
                        ) {
                            dialog.negativeTitle?.let { title ->
                                Row(modifier = Modifier.weight(1f)) {
                                    Spacer(modifier = Modifier.weight(1f))
                                    TextButton(
                                        modifier = Modifier.wrapContentWidth(),
                                        onClick = {
                                            viewModel.dismissDialog(DialogResultType.NEGATIVE)
                                        },
                                        content = {
                                            Texts.BodyLarge(
                                                text  =
                                                if (dialog.autoBtnCapital.negativeButton)
                                                    title.lowercase().capitalizeFirst()
                                                else
                                                    title,
                                                color = AppTheme.current.colors.onSurfaceVariant
                                            )
                                        }
                                    )
                                }
                            }

                            TextButton(
                                modifier = Modifier.wrapContentWidth(),
                                onClick  = {
                                    viewModel.dismissDialog(DialogResultType.POSITIVE)
                                },
                                content  = {
                                    Texts.BodyLarge(
                                        text  =
                                            if (dialog.autoBtnCapital.positiveButton)
                                                dialog.positiveTitle
                                                    .lowercase()
                                                    .capitalizeFirst()
                                            else
                                                dialog.positiveTitle,
                                        color = AppTheme.current.colors.primary,
                                        maxLines = 1
                                    )
                                }
                            )
                        }
                    }
                }
            }
        )
    }
}